import React, { useState, useEffect, useRef, useCallback } from 'react';
import Card from './Card';
import Controls from './Controls';
import SpeedSlider from './SpeedSlider';
import AudioToggle from './AudioToggle';
import '../styles/LoteryGame.css';
import { CARDS_DATA } from '../data/cardsData';

// Jugadores por defecto
const DEFAULT_PLAYERS = ['Alex', 'Kina', 'Nelly', 'Carmen', 'Dulce', 'Lupe', 'Cesar'];

// Frases chistosas
const FUNNY_PHRASES = [
  (name) => `¡Echen su peso todos! Ya te vi ${name}, eres bien mañoso`,
  (name) => `¡Ojo ${name}! No te me hagas el disimulado`,
  (name) => `${name}, ponte las pilas que ya vas perdiendo`,
  (name) => `¡A ver ${name}! Esta vez sí vas a ganar, ¿verdad?`,
  (name) => `${name} ya se está poniendo nervioso, ¡ja ja ja!`,
  (name) => `¡Todos atentos! Menos ${name} que ya anda dormido`,
  (name) => `${name}, si pierdes otra vez te toca pagar los tacos`,
  (name) => `¡Cuídense de ${name}! Trae cara de ganador`,
  (name) => `${name} dice que ahora sí viene la buena suerte`,
  // Nuevas frases picaronas
  (name) => `¡Ahí viene ${name} haciéndose el occiso! Ya páguense`,
  (name) => `¡A ver ${name}! Despiértate que esto ya va a empezar`,
  (name) => `¡Oigan! ${name} ya se quedó dormido otra vez`,
  (name) => `${name}, ¿ya revisaste tu tabla o qué? Llevas ahí pensando media hora`,
  (name) => `Parece que ${name} trae los bolsillos agujerados, ¡echa tu peso!`,
  (name) => `${name} otra vez se está haciendo pato para poner el dinero`,
  (name) => `¡Aguas con ${name}! Ya se le acabó la lana y ni va ganando`,
  (name) => `${name}, mejor vete por un café porque te ves bien dormido`,
  (name) => `${name} trae cara de que ya perdió todo el domingo`,
  (name) => `¡${name} ya despierta! Esto no es hora de siesta`,
  (name) => `${name}, con esa tabla tan fea que tienes, mejor ni le eches ganas`,
  (name) => `¡Todos paguen! Especialmente ${name} que siempre se hace menso`,
  (name) => `${name}, ¿ya checaste bien o nomás estás ahí de adorno?`,
  (name) => `¡A ver ${name}! ¿Cuándo vas a poner de tu bolsa?`,
  (name) => `${name}, esa tabla se ve muy sospechosa, ¿la compraste o la hiciste tú?`,
  (name) => `¡Órale ${name}! Deja de echarle la culpa a las cartas`,
  (name) => `${name}, con esa suerte mejor te hubieras quedado en casa`,
  (name) => `¡${name} ya despertó! Pensé que ibas a roncar todo el juego`,
  (name) => `${name}, ¿trajiste dinero o nomás vienes a calentar el asiento?`,
  (name) => `¡Ahí está ${name} otra vez! Haciéndose el sordo cuando toca pagar`,
  (name) => `${name}, hoy te veo con cara de perdedor, ¿qué te pasó?`,
  (name) => `¡Pónganse todos! Sobre todo ${name} que siempre anda de codo`,
  (name) => `${name}, con esa cara de amargado no vas a ganar ni de chiste`,
  (name) => `¡A ver ${name}! ¿Cuánto llevas perdido hoy? ¿Como doscientos?`,
  (name) => `${name} ya está checando el celular, ya se aburrió de perder`,
  (name) => `¡Órale ${name}! Si no traes pa' la apuesta, mejor di desde ahorita`,
  (name) => `${name}, ¿vas a poner o vas a seguir haciéndote el loco?`,
  (name) => `¡${name} pon atención desde ahorita! Te conozco y siempre te distraes`,
  (name) => `${name}, si sigues así de dormido mejor vete a tu casa a descansar`,
];

// Frases chistosas para ganadores
const WINNER_PHRASES = [
  '¡Ahora sí ya traes para prestarme una lana!',
  '¡Ya te la sabes, invitas los tacos!',
  '¡Esa suerte ya me la debías!',
  '¡Con eso ya puedes pagar lo que debías!',
  '¡Ya sabíamos que ibas a ganar, tramposo!',
  '¡La próxima me toca a mí!',
  '¡Disfruta tu victoria porque no va a durar!',
  '¡Te la rifaste, ahora a compartir!',
  '¡Eso, eso! ¡Que paguen los que perdieron!',
  '¡Ya era hora de que ganaras algo!',
  '¡Con ese dinero ya puedes pagar tu deuda!',
  '¡Guarda ese billete que luego te lo quito!',
  '¡A ver si ahora sí nos invitas!',
  '¡Paga la ronda que ya ganaste!',
];

const LoteryGame = () => {
  // Cargar jugadores del localStorage o usar los por defecto
  const loadPlayers = () => {
    const saved = localStorage.getItem('loteriaPlayers');
    return saved ? JSON.parse(saved) : DEFAULT_PLAYERS;
  };

  const [players, setPlayers] = useState(loadPlayers);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [showPlayerManager, setShowPlayerManager] = useState(false);

  // Guardar jugadores en localStorage cuando cambien
  useEffect(() => {
    localStorage.setItem('loteriaPlayers', JSON.stringify(players));
  }, [players]);

  const [cards, setCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [cardHistory, setCardHistory] = useState([]);
  const gameIntervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const [randomRotation, setRandomRotation] = useState({
    start: -20,
    mid: 8,
    startX: -40,
    midX: 15
  });
  const [showStats, setShowStats] = useState(false);

  // Cargar estadísticas del localStorage
  const loadStats = useCallback(() => {
    const saved = localStorage.getItem('loteriaStats');
    if (saved) {
      return JSON.parse(saved);
    }
    // Inicializar stats para cada jugador
    const initialStats = {};
    players.forEach(player => {
      initialStats[player] = { wins: 0, losses: 0 };
    });
    return initialStats;
  }, [players]);

  const [playerStats, setPlayerStats] = useState(loadStats);

  // Guardar stats en localStorage cuando cambien
  useEffect(() => {
    localStorage.setItem('loteriaStats', JSON.stringify(playerStats));
  }, [playerStats]);

  // Generar valores aleatorios para cada carta nueva
  useEffect(() => {
    setRandomRotation({
      start: Math.random() > 0.5 ? -(15 + Math.random() * 15) : (15 + Math.random() * 15), // -30 a -15 o 15 a 30
      mid: Math.random() > 0.5 ? -(5 + Math.random() * 8) : (5 + Math.random() * 8), // -13 a -5 o 5 a 13
      startX: Math.random() > 0.5 ? -(30 + Math.random() * 30) : (30 + Math.random() * 30), // -60 a -30 o 30 a 60
      midX: Math.random() > 0.5 ? -(10 + Math.random() * 15) : (10 + Math.random() * 15), // -25 a -10 o 10 a 25
    });
  }, [currentCardIndex]);

  // Fix para Edge: Forzar carga de voces y mantener contexto activo
  useEffect(() => {
    // Cargar voces inmediatamente
    if ('speechSynthesis' in window) {
      speechSynthesis.getVoices();
      
      // Evento cuando las voces están listas
      speechSynthesis.onvoiceschanged = () => {
        console.log('✅ Voces cargadas:', speechSynthesis.getVoices().length);
      };
    }
  }, []);

  const speakCard = useCallback((cardName, onComplete) => {
    if (!audioEnabled) {
      if (onComplete) onComplete();
      return;
    }
    
    if ('speechSynthesis' in window) {
      // IMPORTANTE: Cancelar síntesis anterior
      speechSynthesis.cancel();
      
      // Pequeño delay para que Edge procese el cancel
      setTimeout(() => {
        // Crear utterance
        const utterance = new SpeechSynthesisUtterance(cardName);
        
        // CONFIGURACIÓN CRÍTICA - VOLUMEN AL 100%
        utterance.volume = 1.0;  // Máximo volumen
        utterance.rate = 1.0;    // Velocidad normal (antes 0.9)
        utterance.pitch = 1.1;   // Tono más agudo para voces graves
        utterance.lang = 'es-MX';
        
        // Obtener voces disponibles
        const voices = speechSynthesis.getVoices();
        
        // Buscar voces femeninas de mejor calidad
        const spanishVoice = 
          voices.find(v => v.name.includes('Mónica') && v.lang.startsWith('es')) ||     // Mónica
          voices.find(v => v.name.includes('Monica') && v.lang.startsWith('es')) ||    // Monica
          voices.find(v => v.name.includes('Google español de México')) ||             // Google
          voices.find(v => v.name.includes('Angelica') && v.lang.startsWith('es')) ||  // Angelica
          voices.find(v => v.name.includes('Isabel') && v.lang.startsWith('es')) ||    // Isabel
          voices.find(v => v.lang === 'es-MX' && v.name.includes('Premium')) ||        // Premium
          voices.find(v => v.lang === 'es-MX') ||                                       // Cualquier es-MX
          voices.find(v => v.lang.startsWith('es-'));
        
        if (spanishVoice) {
          utterance.voice = spanishVoice;
          console.log('🎙️ Voz:', spanishVoice.name, '| Vol:', utterance.volume);
        }
        
        // Eventos para debug
        utterance.onstart = () => {
          console.log('▶️ HABLANDO:', cardName, '| Volumen:', utterance.volume);
        };
        utterance.onend = () => {
          console.log('✅ Terminó');
          if (onComplete) onComplete();
        };
        utterance.onerror = (e) => {
          console.error('❌ Error:', e.error);
          if (onComplete) onComplete();
        };
        
        // Hablar
        speechSynthesis.speak(utterance);
        
        // Debug: verificar estado
        console.log('🔊 Speaking:', speechSynthesis.speaking, '| Pending:', speechSynthesis.pending);
      }, 50);
    } else {
      if (onComplete) onComplete();
    }
  }, [audioEnabled]);

  // Funciones para Wake Lock (mantener pantalla encendida en mobile)
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('🔒 Wake Lock activado - pantalla permanecerá encendida');
        
        wakeLockRef.current.addEventListener('release', () => {
          console.log('🔓 Wake Lock liberado');
        });
      } catch (err) {
        console.error('❌ Error al activar Wake Lock:', err);
      }
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current !== null) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (err) {
        console.error('❌ Error al liberar Wake Lock:', err);
      }
    }
  }, []);

  // Reactivar wake lock si la página vuelve a estar visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (wakeLockRef.current !== null && document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestWakeLock]);

  // Liberar wake lock al desmontar el componente
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  const shuffleCards = useCallback(() => {
    const shuffled = [...CARDS_DATA].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setCurrentCardIndex(0);
    setIsPlaying(false);
    setHasStarted(false);
    setCardHistory([]);
    // Liberar wake lock cuando se reinicia el juego
    releaseWakeLock();
  }, [releaseWakeLock]);

  useEffect(() => {
    shuffleCards();
  }, [shuffleCards]);

  useEffect(() => {
    if (!isPlaying || cards.length === 0) {
      return;
    }

    gameIntervalRef.current = setInterval(() => {
      setCurrentCardIndex((prevIndex) => {
        if (prevIndex + 1 < cards.length) {
          const nextIndex = prevIndex + 1;
          return nextIndex;
        } else {
          setIsPlaying(false);
          return prevIndex;
        }
      });
    }, speed * 1000);

    return () => {
      if (gameIntervalRef.current) {
        clearInterval(gameIntervalRef.current);
      }
    };
  }, [isPlaying, speed, cards]);

  // Efecto separado para manejar el historial y el speech cuando cambia la carta
  const prevCardIndexRef = useRef(currentCardIndex);
  useEffect(() => {
    if (!hasStarted || cards.length === 0) return;
    
    // Solo ejecutar cuando el índice realmente cambió
    if (prevCardIndexRef.current !== currentCardIndex && currentCardIndex > 0) {
      // Agregar la carta anterior al historial
      const prevCard = cards[currentCardIndex - 1];
      if (prevCard) {
        setCardHistory(prev => {
          // Verificar que no exista ya esta carta con este índice
          const exists = prev.some(c => c.id === prevCard.id && c.historyIndex === currentCardIndex - 1);
          if (exists) return prev;
          
          const cardWithId = { ...prevCard, historyIndex: currentCardIndex - 1, timestamp: Date.now() };
          const newHistory = [cardWithId, ...prev];
          return newHistory.slice(0, 5);
        });
      }
      
      // Hablar la carta actual con delay para que coincida con la animación
      if (audioEnabled && cards[currentCardIndex]) {
        const animationDelay = (speed + 0.3) * 0.3 * 1000; // 30% de la duración total
        setTimeout(() => {
          speakCard(cards[currentCardIndex].name);
        }, animationDelay);
      }
    }
    
    prevCardIndexRef.current = currentCardIndex;
  }, [currentCardIndex, hasStarted, cards, audioEnabled, speakCard, speed]);

  const handleStart = useCallback(() => {
    // Activar wake lock para mantener pantalla encendida
    requestWakeLock();
    
    if (audioEnabled && cards[currentCardIndex]) {
      // Decidir si decir frase chistosa (40% de las veces)
      const shouldSayFunnyPhrase = Math.random() < 0.4;
      
      const startGame = () => {
        // Decir "SE VA Y SE CORRE CON" y esperar a que termine
        speakCard('SE VA Y SE CORRE CON', () => {
          // Ahora sí, INICIAR EL JUEGO
          setHasStarted(true);
          setIsPlaying(true);
          
          // Decir la primera carta
          const animationDelay = (speed + 0.3) * 0.3 * 1000;
          setTimeout(() => {
            speakCard(cards[currentCardIndex].name);
          }, animationDelay);
        });
      };
      
      if (shouldSayFunnyPhrase) {
        // Escoger jugador y frase al azar
        const randomPlayer = players[Math.floor(Math.random() * players.length)];
        const randomPhrase = FUNNY_PHRASES[Math.floor(Math.random() * FUNNY_PHRASES.length)];
        const funnyMessage = randomPhrase(randomPlayer);
        
        // Decir frase chistosa y esperar a que termine
        speakCard(funnyMessage, startGame);
      } else {
        // Ir directo a "SE VA Y SE CORRE CON"
        startGame();
      }
    } else {
      // Si no hay audio, iniciar inmediatamente
      setHasStarted(true);
      setIsPlaying(true);
    }
  }, [cards, currentCardIndex, audioEnabled, speakCard, speed, players, requestWakeLock]);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    if (gameIntervalRef.current) {
      clearInterval(gameIntervalRef.current);
    }
  }, []);

  const handleWinner = useCallback((winners = null, isEmpate = false) => {
    handleStop();
    
    // Convertir a array si es un solo ganador (compatibilidad)
    const winnerArray = Array.isArray(winners) ? winners : (winners ? [winners] : []);
    
    if (winnerArray.length > 0) {
      // Puntos a otorgar por ganador
      const pointsPerWinner = isEmpate ? 0.5 : 1;
      
      // Actualizar estadísticas
      setPlayerStats(prev => {
        const updated = { ...prev };
        players.forEach(player => {
          if (winnerArray.includes(player)) {
            // Sumar puntos al ganador
            updated[player] = { 
              ...updated[player], 
              wins: (updated[player]?.wins || 0) + pointsPerWinner 
            };
          } else {
            // Sumar derrota a los que no ganaron
            updated[player] = { 
              ...updated[player], 
              losses: (updated[player]?.losses || 0) + 1 
            };
          }
        });
        return updated;
      });
      
      // Anunciar ganador(es)
      if (audioEnabled) {
        const random = Math.random();
        let message;
        
        if (winnerArray.length > 1) {
          // Múltiples ganadores (empate)
          const names = winnerArray.join(' y ');
          if (random < 0.20) {
            const randomWinnerPhrase = WINNER_PHRASES[Math.floor(Math.random() * WINNER_PHRASES.length)];
            message = `¡Empate entre ${names}! ${randomWinnerPhrase}`;
          } else if (random < 0.50) {
            message = `¡Felicidades ${names}! ¡Empataron esta ronda!`;
          } else {
            message = `¡Buenas para ${names}!`;
          }
        } else {
          // Un solo ganador
          const winnerName = winnerArray[0];
          if (random < 0.20) {
            const randomWinnerPhrase = WINNER_PHRASES[Math.floor(Math.random() * WINNER_PHRASES.length)];
            message = `¡Felicidades ${winnerName}! ¡Ganaste esta ronda! ${randomWinnerPhrase}`;
          } else if (random < 0.50) {
            message = `¡Felicidades ${winnerName}! ¡Ganaste esta ronda!`;
          } else {
            message = `¡Buenas ${winnerName}!`;
          }
        }
        
        speakCard(message);
      }
      
      // REINICIAR el juego después de 2 segundos
      setTimeout(() => {
        shuffleCards();
      }, 2000);
    }
  }, [handleStop, audioEnabled, speakCard, players, shuffleCards]);

  const handleReset = useCallback(() => {
    handleStop();
    shuffleCards();
  }, [handleStop, shuffleCards]);

  const handleSpeedChange = useCallback((newSpeed) => {
    setSpeed(newSpeed);
  }, []);

  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => !prev);
  }, []);

  const currentCard = cards[currentCardIndex];

  return (
    <div className="lotery-game">
      <div className="game-layout">
        {/* Histórico de cartas - Izquierda */}
        <div className="card-history">
          <h3 className="history-title">Últimas Cartas</h3>
          <div className="history-cards">
            {cardHistory.length > 0 ? (
              cardHistory.map((card, index) => (
                <div key={`${card.id}-${card.historyIndex}-${index}`} className="history-card-item">
                  <img 
                    src={`/cartas/${card.image}`} 
                    alt={card.name}
                    className="history-card-img"
                  />
                  <p className="history-card-name">{card.name}</p>
                </div>
              ))
            ) : (
              <p className="history-empty">No hay cartas jugadas</p>
            )}
          </div>
        </div>

        {/* Carta actual - Centro */}
        <div className="game-center">
          <div className="card-display" onClick={() => isPlaying && window.innerWidth <= 768 && handleStop()}>
            {hasStarted && currentCard ? (
              <Card 
                key={currentCardIndex}
                cardName={currentCard.name}
                cardImage={currentCard.image}
                animationDuration={speed}
                randomRotation={randomRotation}
              />
            ) : (
              <div className="card-back">
                <div className="card-back-pattern"></div>
              </div>
            )}
          </div>
          <div className="game-info">
            <p className="card-counter">
              {hasStarted ? `Carta ${currentCardIndex + 1} de ${cards.length}` : 'Presiona Iniciar para comenzar'}
            </p>
          </div>
        </div>

        {/* Controles - Derecha */}
        <div className="game-controls-panel">
          <AudioToggle audioEnabled={audioEnabled} onToggle={toggleAudio} />
          
          <div className="controls-section">
            <SpeedSlider 
              speed={speed}
              onSpeedChange={handleSpeedChange}
              disabled={isPlaying}
            />
          </div>

          <div className="controls-section">
            <Controls
              isPlaying={isPlaying}
              hasStarted={hasStarted}
              onStart={handleStart}
              onStop={handleStop}
              onWinner={handleWinner}
              onReset={handleReset}
              players={players}
            />
          </div>

          <button 
            className="btn btn-stats"
            onClick={() => setShowPlayerManager(!showPlayerManager)}
            style={{ marginTop: '10px', width: '100%', background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}
          >
            👥 {showPlayerManager ? 'Ocultar' : 'Gestionar'} Jugadores
          </button>

          {showPlayerManager && (
            <div className="player-manager" style={{ 
              marginTop: '15px', 
              background: 'rgba(15, 15, 35, 0.8)',
              borderRadius: '16px',
              padding: '15px'
            }}>
              <h3 style={{ color: '#e0e7ff', fontSize: '0.9rem', marginBottom: '10px', textAlign: 'center' }}>👥 JUGADORES</h3>
              
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newPlayerName.trim()) {
                      if (!players.includes(newPlayerName.trim())) {
                        setPlayers([...players, newPlayerName.trim()]);
                        setNewPlayerName('');
                      } else {
                        alert('Este jugador ya existe');
                      }
                    }
                  }}
                  placeholder="Nombre del jugador"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    marginBottom: '8px'
                  }}
                />
                <button
                  onClick={() => {
                    if (newPlayerName.trim()) {
                      if (!players.includes(newPlayerName.trim())) {
                        setPlayers([...players, newPlayerName.trim()]);
                        setNewPlayerName('');
                      } else {
                        alert('Este jugador ya existe');
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ➕ Agregar Jugador
                </button>
              </div>

              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {players.map((player, index) => (
                  <div key={index} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginBottom: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span style={{ color: '#fff', fontSize: '0.85rem' }}>👤 {player}</span>
                    <button
                      onClick={() => {
                        if (players.length > 1) {
                          setPlayers(players.filter((_, i) => i !== index));
                          // Limpiar stats del jugador eliminado
                          setPlayerStats(prev => {
                            const updated = { ...prev };
                            delete updated[player];
                            return updated;
                          });
                        } else {
                          alert('Debe haber al menos un jugador');
                        }
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        color: '#fca5a5',
                        fontSize: '0.7rem',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ Eliminar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button 
            className="btn btn-stats"
            onClick={() => setShowStats(!showStats)}
            style={{ marginTop: '10px', width: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            📊 {showStats ? 'Ocultar' : 'Ver'} Estadísticas
          </button>

          {showStats && (
            <div className="stats-panel" style={{ 
              marginTop: '15px', 
              background: 'rgba(15, 15, 35, 0.8)',
              borderRadius: '16px',
              padding: '15px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <h3 style={{ color: '#e0e7ff', fontSize: '0.9rem', marginBottom: '10px', textAlign: 'center' }}>🏆 TABLA DE POSICIONES 🏆</h3>
              {Object.entries(playerStats)
                .sort(([, a], [, b]) => (b.wins || 0) - (a.wins || 0))
                .map(([name, stats], index) => (
                  <div key={name} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: '600' }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '👤'} {name}
                    </span>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem' }}>
                      <span style={{ color: '#10b981' }}>
                        ✅ {(stats.wins || 0).toFixed(1).replace('.0', '')}
                      </span>
                      <span style={{ color: '#ef4444' }}>❌ {stats.losses || 0}</span>
                    </div>
                  </div>
                ))}
              <button
                onClick={() => {
                  if (window.confirm('¿Seguro que quieres resetear todas las estadísticas?')) {
                    const reset = {};
                    players.forEach(p => { reset[p] = { wins: 0, losses: 0 }; });
                    setPlayerStats(reset);
                  }
                }}
                style={{
                  marginTop: '10px',
                  width: '100%',
                  padding: '8px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  color: '#fca5a5',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                🗑️ Resetear Estadísticas
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoteryGame;
